import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { DateISO } from "../utils/DateISO";

/**
 * Options for the useDateRangePicker hook
 */
export interface UseDateRangePickerOptions {
  /**
   * Initial start date in ISO format
   */
  initialStartDate?: DateISO | null;

  /**
   * Initial end date in ISO format
   */
  initialEndDate?: DateISO | null;

  /**
   * Callback function when dates change
   */
  onChange?: (startDate: DateISO | null, endDate: DateISO | null) => void;
}

/**
 * Internal date range structure using dayjs for Ant Design compatibility
 */
interface DateRange {
  start: Dayjs | null;
  end: Dayjs | null;
}

/**
 * Custom hook for managing date range selection
 * Handles conversion between DateISO and dayjs formats for compatibility with Ant Design DatePicker
 */
export function useDateRangePicker({
  initialStartDate,
  initialEndDate,
  onChange,
}: UseDateRangePickerOptions = {}) {
  // State using dayjs for Ant Design DatePicker compatibility
  const [dateRange, setDateRange] = useState<DateRange>({
    start: initialStartDate ? dayjs(initialStartDate.toString()) : null,
    end: initialEndDate ? dayjs(initialEndDate.toString()) : null,
  });

  // Update internal state when props change
  useEffect(() => {
    setDateRange({
      start: initialStartDate ? dayjs(initialStartDate.toString()) : null,
      end: initialEndDate ? dayjs(initialEndDate.toString()) : null,
    });
  }, [initialStartDate, initialEndDate]);

  // Handle date range changes from the DatePicker component
  const handleDateRangeChange = useCallback(
    (
      dates: [Dayjs | null, Dayjs | null] | null,
      _dateStrings: [string, string],
    ) => {
      if (dates) {
        const [start, end] = dates;

        setDateRange({
          start: start?.isValid() ? start : null,
          end: end?.isValid() ? end : null,
        });

        if (onChange) {
          // Convert dayjs to DateISO for external API
          const startDate = start?.isValid()
            ? DateISO.fromString(start.format("YYYY-MM-DD"))
            : null;

          const endDate = end?.isValid()
            ? DateISO.fromString(end.format("YYYY-MM-DD"))
            : null;

          onChange(startDate, endDate);
        }
      } else {
        // Handle cleared date range
        setDateRange({ start: null, end: null });
        if (onChange) {
          onChange(null, null);
        }
      }
    },
    [onChange],
  );

  // Helper function to clear date range
  const clearDateRange = useCallback(() => {
    setDateRange({ start: null, end: null });
    if (onChange) {
      onChange(null, null);
    }
  }, [onChange]);

  // Format dates for display
  const formattedStartDate = dateRange.start
    ? dateRange.start.format("MM/DD/YYYY")
    : "";

  const formattedEndDate = dateRange.end
    ? dateRange.end.format("MM/DD/YYYY")
    : "";

  return {
    dateRange,
    setDateRange: handleDateRangeChange,
    clearDateRange,
    formattedStartDate,
    formattedEndDate,
  };
}
